/**
 * UserCard Component
 *
 * Displays a user with their details and action buttons.
 */

import React from 'react';
import { User, Mail, Calendar, MoreVertical, Edit, Shield, Ban, CheckCircle, Clock, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { User as UserType, UserRole, UserStatus } from '@/types';

interface UserCardProps {
  user: UserType;
  onEdit: () => void;
  onStatusChange: (status: UserStatus) => void;
}

const statusColors: Record<UserStatus, string> = {
  [UserStatus.ACTIVE]: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  [UserStatus.INACTIVE]: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400',
  [UserStatus.SUSPENDED]: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  [UserStatus.PENDING]: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
};

const roleColors: Record<UserRole, string> = {
  [UserRole.ADMIN]: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  [UserRole.USER]: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  [UserRole.VIEWER]: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400',
  [UserRole.MODERATOR]: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
};

const statusIcons: Record<UserStatus, React.ReactNode> = {
  [UserStatus.ACTIVE]: <CheckCircle className="h-3 w-3" />,
  [UserStatus.INACTIVE]: <XCircle className="h-3 w-3" />,
  [UserStatus.SUSPENDED]: <Ban className="h-3 w-3" />,
  [UserStatus.PENDING]: <Clock className="h-3 w-3" />,
};

export const UserCard: React.FC<UserCardProps> = ({ user, onEdit, onStatusChange }) => {
  const formatDate = (date: Date | string | undefined) => {
    if (!date) return 'Never';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatRelativeTime = (date: Date | string | undefined) => {
    if (!date) return 'Never';
    const now = new Date();
    const then = new Date(date);
    const diffMs = now.getTime() - then.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatDate(date);
  };

  return (
    <div className="border border-border rounded-lg p-4 bg-card">
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          {/* Avatar */}
          {user.avatar ? (
            <img src={user.avatar} alt={user.username} className="h-12 w-12 rounded-full" />
          ) : (
            <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
              <User className="h-6 w-6 text-primary" />
            </div>
          )}

          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-semibold">
                {user.firstName} {user.lastName}
              </h3>
              {user.role === UserRole.ADMIN && (
                <Shield className="h-4 w-4 text-purple-500" />
              )}
            </div>
            <div className="text-sm text-muted-foreground">@{user.username}</div>
            <div className="flex items-center gap-1 text-sm text-muted-foreground mt-1">
              <Mail className="h-3 w-3" />
              <span>{user.email}</span>
            </div>
          </div>
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={onEdit}>
              <Edit className="h-4 w-4 mr-2" />
              Edit User
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-xs text-muted-foreground">
              Change Status
            </DropdownMenuLabel>
            <DropdownMenuItem
              onClick={() => onStatusChange(UserStatus.ACTIVE)}
              disabled={user.status === UserStatus.ACTIVE}
            >
              <CheckCircle className="h-4 w-4 mr-2 text-green-500" />
              Active
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => onStatusChange(UserStatus.INACTIVE)}
              disabled={user.status === UserStatus.INACTIVE}
            >
              <XCircle className="h-4 w-4 mr-2 text-gray-500" />
              Inactive
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => onStatusChange(UserStatus.SUSPENDED)}
              disabled={user.status === UserStatus.SUSPENDED}
            >
              <Ban className="h-4 w-4 mr-2 text-red-500" />
              Suspended
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => onStatusChange(UserStatus.PENDING)}
              disabled={user.status === UserStatus.PENDING}
            >
              <Clock className="h-4 w-4 mr-2 text-yellow-500" />
              Pending
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Badges and Metadata */}
      <div className="mt-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {/* Role Badge */}
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${roleColors[user.role]}`}>
            {user.role}
          </span>
          {/* Status Badge */}
          <span className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusColors[user.status]}`}>
            {statusIcons[user.status]}
            {user.status}
          </span>
        </div>

        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          <div className="flex items-center gap-1">
            <Calendar className="h-3 w-3" />
            <span>Joined {formatDate(user.createdAt)}</span>
          </div>
          {user.lastLoginAt && (
            <div>
              <span>Last login {formatRelativeTime(user.lastLoginAt)}</span>
            </div>
          )}
        </div>
      </div>

      {/* Bio */}
      {user.bio && (
        <div className="mt-3 text-sm text-muted-foreground line-clamp-2">{user.bio}</div>
      )}

      {/* Actions */}
      <div className="mt-4 flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={onEdit}>
          <Edit className="h-4 w-4 mr-2" />
          Edit
        </Button>
        {user.status === UserStatus.ACTIVE ? (
          <Button
            variant="outline"
            size="sm"
            className="text-red-600 hover:text-red-700"
            onClick={() => onStatusChange(UserStatus.SUSPENDED)}
          >
            <Ban className="h-4 w-4 mr-2" />
            Suspend
          </Button>
        ) : user.status === UserStatus.SUSPENDED ? (
          <Button
            variant="outline"
            size="sm"
            className="text-green-600 hover:text-green-700"
            onClick={() => onStatusChange(UserStatus.ACTIVE)}
          >
            <CheckCircle className="h-4 w-4 mr-2" />
            Activate
          </Button>
        ) : null}
      </div>
    </div>
  );
};

export default UserCard;
